import axios from "axios";
import {
  CampaignDetails,
  CampaignRegisteredChannel,
  EstimatedTotalMembersRequest,
} from "../models/campaign";
import { ChannelEnum, StatusEnum } from "../constants/enums";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const API_BASE_GETZPASS_URL = process.env.REACT_APP_API_GETZPASS_URL || "";
const API_BASE_MESSAGING_URL = process.env.REACT_APP_API_MESSAGING_URL || "";
const API_GETZPASS_KEY = process.env.REACT_APP_API_ACCESS_GETZPASS_KEY || "";

const isDevelopment =
  API_BASE_URL === "" ||
  API_BASE_URL === "https://sit-api-crm.getzpay.com" ||
  API_BASE_URL === "https://sit-applepay.getzpay.com" ||
  API_BASE_URL === "https://staging-api.getzpay.com" ||
  API_BASE_URL.includes("local");

export const campaignApi = {
  // CRM CAMPAIGN
  getAllCampaign: async (token: string) => {
    return axios.post(`${API_BASE_URL}/v1/RFM/GetListCampaign`, null, {
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
    });
  },
  getAllCampaignDetails: async (token: string, brandId: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/ListAllCampaignDetails`,
      { brandId: brandId },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getRegisteredChannel: async (token: string, brandId: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetRegisteredChannel`,
      { brandId: brandId },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  createCampaignRegisteredChannel: async (
    token: string,
    registeredChannel: CampaignRegisteredChannel,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/AddCampaignRegisteredChannel`,
      registeredChannel,
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  reactiveCampaignRegisteredChannelStatus: async (
    token: string,
    id: number,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignRegisteredChannelStatus`,
      { Id: id, Status: 1 },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  deleteCampaignRegisteredChannel: async (token: string, id: number) => {
    const body = new URLSearchParams({ Id: String(id) });
    return axios.post(
      `${API_BASE_URL}/v1/RFM/DeleteCampaignRegisteredChannel`,
      body,
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    );
  },
  updateCampaignRegisteredChannelEmail: async (
    token: string,
    brandId: string,
    email: string,
  ) => {
    const body = new URLSearchParams({ BrandId: brandId, Email: email });
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignRegisteredChannelEmail`,
      body,
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    );
  },
  generateQR: async (token: string, clientId: string) => {
    return axios.get(
      `${API_BASE_MESSAGING_URL}/whatsapp/client/qr/${clientId}`,
      {
        headers: {
          "x-secret-token": token,
          "Content-Type": "application/json",
        },
      },
    );
  },

  initClientId: async (token: string, clientId: string) => {
    return axios.post(
      `${API_BASE_MESSAGING_URL}/whatsapp/client/init`,
      { clientId: clientId },
      {
        headers: {
          "x-secret-token": token,
          "Content-Type": "application/json",
        },
      },
    );
  },
  checkActivate: async (token: string, clientId: string) => {
    return axios.post(
      `${API_BASE_MESSAGING_URL}/whatsapp/client/health`,
      { clientId: clientId },
      {
        headers: {
          "x-secret-token": token,
          "Content-Type": "application/json",
        },
      },
    );
  },
  checkAvailableCampaignName: async (
    token: string,
    name: string,
    id?: number,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/AvailableCampaignName`,
      { Name: name, Id: id },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  createCampaignDetails: async (
    token: string,
    campaignDetails: CampaignDetails,
    exclusionFile?: File,
  ) => {
    const form = new FormData();
    form.append("request", JSON.stringify(campaignDetails));
    if (exclusionFile) {
      form.append("exclusionFile", exclusionFile, exclusionFile.name);
    }
    return axios.post(
      `${API_BASE_URL}/v1/RFM/CreateCampaignDetails`,
      form,
      {
        headers: {
          Authorization: `Basic ${token}`,
        },
        params: {
          Redshift: isDevelopment ? false : true,
        },
      },
    );
  },
  createCampaignLog: async (token: string, id: number) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/CreateCampaignLog`,
      { Id: id },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },

  updateCampaignDetails: async (
    token: string,
    campaignDetails: CampaignDetails,
    exclusionFile?: File,
  ) => {
    const form = new FormData();
    form.append("request", JSON.stringify(campaignDetails));
    if (exclusionFile) {
      form.append("exclusionFile", exclusionFile, exclusionFile.name);
    }
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignDetails`,
      form,
      {
        headers: {
          Authorization: `Basic ${token}`,
        },
      },
    );
  },
  updateCampaignDetailsStatus: async (
    token: string,
    id: number,
    status: StatusEnum,
    isBilled: boolean = false,
    referenceCode?: string,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignDetailsStatus`,
      {
        Id: id,
        CampaignRFMStatus: status,
        IsBilled: isBilled,
        ...(referenceCode ? { ReferenceCode: referenceCode } : {}),
        ...(isDevelopment ? { UsingRedshift: false } : { UsingRedshift: true }),
      },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getCampaignDetailsById: async (token: string, id: number) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetCampaignDetailsById`,
      { Id: id },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  deleteCampaignDetails: async (token: string, id: number) => {
    return axios.delete(`${API_BASE_URL}/v1/RFM/DeleteCampaignDetails`, {
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
      data: { Id: id },
    });
  },
  estimatedTotalMember: async (
    token: string,
    estimatedTotalMembersRequest: EstimatedTotalMembersRequest,
    exclusionFile?: File,
  ) => {
    const payload: EstimatedTotalMembersRequest = {
      ...estimatedTotalMembersRequest,
      IncludeListMember: true,
      ExcludeMembers: null,
      ...(isDevelopment ? { UsingRedshift: false } : { UsingRedshift: true }),
    };
    const form = new FormData();
    form.append("request", JSON.stringify(payload));
    if (exclusionFile) {
      form.append("exclusionFile", exclusionFile, exclusionFile.name);
    }
    return axios.post(`${API_BASE_URL}/v1/RFM/EstimatedTotalMember`, form, {
      headers: {
        Authorization: `Basic ${token}`,
      },
    });
  },
  getAllPostalDistrict: async (token: string) => {
    return axios.post(`${API_BASE_URL}/v1/RFM/GetAllPostalDistricts`, null, {
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
    });
  },
  uploadCampaignImage: async (token: string, file: File) => {
    const form = new FormData();
    form.append("CampaignImage", file, file.name);

    return axios.post(`${API_BASE_URL}/v1/RFM/UploadCampaignImage`, form, {
      headers: {
        Authorization: `Basic ${token}`,
        Accept: "application/json",
      },
    });
  },
  getBlockedCampaignDateRanges: async (
    token: string,
    brandId: string,
    channel: ChannelEnum | null,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetBlockedCampaignDateRanges`,
      { Channel: channel, brandId: brandId },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getListAccountBrand: async (token: string, brandId: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetListAccountBrand`,
      { brandId: brandId },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getTopPerformingCampaigns: async (
    token: string,
    brandId: string,
    count: number | null,
  ) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetTopPerformingCampaigns`,
      { BrandId: brandId, Count: count },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getCampaignInfo: async (token: string, brandId: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/GetCampaignInfo`,
      { brandId: brandId },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  getAllVouchers: async (token: string) => {
    const decodedToken = atob(token);

    const cleanToken = decodedToken.split(":")[0];

    const now = new Date();
    const currentDateTime = now.toISOString().slice(0, 19).replace("T", " ");

    return axios.post(
      `${API_BASE_URL}/Reward/GetAllVouchers`,
      {
        Authorization: cleanToken,
        CurrentDateTime: currentDateTime,
        isFetchFromMerchantGroup: "true",
        isFetchCampaignVoucher: "true",
      },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  },
  getAllEVouchers: async (email: string) => {
    return axios.post(
      `${API_BASE_GETZPASS_URL}/api/e-vouchers/get-list`,
      {
        apiAccessKey: API_GETZPASS_KEY,
        email: email,
        isActive: "true",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  },
  getAllMessageTemplates: async (token: string) => {
    return axios.post(`${API_BASE_URL}/v1/RFM/GetAllCampaignTemplates`, null, {
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
    });
  },
  checkAccountCreditCard: async (token: string, email: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/CheckAccountCreditCard`,
      { Email: email },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  processPayment: async (token: string, campaignId: number, _email: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignBilled`,
      { Id: campaignId, IsBilled: 1 },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  updateCampaignBilled: async (token: string, campaignId: number, referenceCode?: string) => {
    return axios.post(
      `${API_BASE_URL}/v1/RFM/UpdateCampaignBilled`,
      { Id: campaignId, IsBilled: 1, ...(referenceCode ? { ReferenceCode: referenceCode } : {}) },
      {
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  },
  downloadRecipient: async (token: string, id: number, campaignRunLogId?: number) => {
    const requestBody: any = {
      Id: id,
      ...(campaignRunLogId != null && { CampaignRunLogId: campaignRunLogId }),
    };
    const endpoint = `${API_BASE_URL}/v1/RFM/DownloadRecipient`;
    return axios.post(endpoint, requestBody, {
      responseType: "blob",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
    });
  },
  rescheduleCampaign: async (
    token: string,
    id: number,
    campaignStart: string,
    campaignEnd: string,
  ) => {
    const requestBody: any = {
      Id: id,
      CampaignStart: campaignStart,
      CampaignEnd: campaignEnd,
    };
    const endpoint = `${API_BASE_URL}/v1/RFM/RescheduleCampaignSendDate`;
    return axios.post(endpoint, requestBody, {
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
    });
  },
  getEmailPreview: async (
    token: string,
    type: number | undefined,
    brandId: string,
    voucherId?: number,
  ) => {
    const params: Record<string, any> = { brandId };
    if (type !== undefined) params.type = type;
    if (voucherId) params.voucherId = voucherId;
    return axios.get(`${API_BASE_URL}/v1/rfm/EmailPreview`, {
      params,
      headers: { Authorization: `Basic ${token}` },
      responseType: "text",
    });
  },
};
